/**
 * React hooks for subscribing to the Ghostwriter external store.
 *
 * Uses `useSyncExternalStore` so any component in any React tree can
 * subscribe to the same shared state. No provider wrapper is required.
 */

import type { AsyncState } from "./AsyncState";
import type { ScheduleApplyConsentStatusResponse } from "../models/ScheduleApplyConsentStatus";
import type { ScheduleApplyJob } from "../models/ScheduleApplyJob";

import {
  type DraftFieldValue,
  type DraftState,
  subscribe,
  getSnapshot,
  getServerSnapshot,
  setJobsState,
  setConsentStatusState,
  updateJob,
  beginDraft,
  updateDraftField,
  getDraftFieldUpdates,
  discardDraft,
  validateDraftField,
  validateAllDraftFields,
  resetAttemptedFields,
  setAutoAppliableJobKeys,
  addGroupInstance,
  removeGroupInstance,
} from "./GhostwriterStore";

import { useSyncExternalStore } from "react";

export interface UseGhostwriterResult {
  autoAppliableJobKeys: AsyncState<Set<string>>;
  jobs: AsyncState<ScheduleApplyJob[]>;
  consentStatus: AsyncState<ScheduleApplyConsentStatusResponse>;
  draft: DraftState | null;

  setAutoAppliableJobKeys: (state: AsyncState<Set<string>>) => void;
  setJobsState: (state: AsyncState<ScheduleApplyJob[]>) => void;
  setConsentStatusState: (state: AsyncState<ScheduleApplyConsentStatusResponse>) => void;
  updateJob: (applicationId: string, updates: Partial<ScheduleApplyJob>) => void;

  beginDraft: (applicationId: string) => void;
  updateDraftField: (fieldId: string, value: DraftFieldValue) => void;
  getDraftFieldUpdates: () => {
    applicationId: string;
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>;
  } | null;
  discardDraft: () => void;
}

/**
 * Subscribe to the full Ghostwriter store. Re-renders when any part
 * of the store changes. For narrower subscriptions, use the
 * dedicated selector hooks.
 */
export function useGhostwriter(): UseGhostwriterResult {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    ...state,
    setAutoAppliableJobKeys,
    setJobsState,
    setConsentStatusState,
    updateJob,
    beginDraft,
    updateDraftField,
    getDraftFieldUpdates,
    discardDraft,
  };
}

const getIsGhostwriterActiveSnapshot = () => getSnapshot().isGhostwriterActive;
const getIsGhostwriterPhaseASnapshot = () => getSnapshot().isGhostwriterPhaseA;
const getIsGhostwriterPhaseBSnapshot = () => getSnapshot().isGhostwriterPhaseB;
const getIsGhostwriterPhaseCSnapshot = () => getSnapshot().isGhostwriterPhaseC;
const getIsGhostwriterPhaseDSnapshot = () => getSnapshot().isGhostwriterPhaseD;
const getIsGhostwriterPhaseESnapshot = () => getSnapshot().isGhostwriterPhaseE;
const getPhaseEAllowlistJobKeysSnapshot = () => getSnapshot().phaseEAllowlistJobKeys;
const getAutoAppliableJobKeysSnapshot = () => getSnapshot().autoAppliableJobKeys;
const getJobsSnapshot = () => getSnapshot().jobs;
const getConsentStatusSnapshot = () => getSnapshot().consentStatus;
const getDraftSnapshot = () => getSnapshot().draft;

const getIsGhostwriterActiveServerSnapshot = () => getServerSnapshot().isGhostwriterActive;
const getIsGhostwriterPhaseAServerSnapshot = () => getServerSnapshot().isGhostwriterPhaseA;
const getIsGhostwriterPhaseBServerSnapshot = () => getServerSnapshot().isGhostwriterPhaseB;
const getIsGhostwriterPhaseCServerSnapshot = () => getServerSnapshot().isGhostwriterPhaseC;
const getIsGhostwriterPhaseDServerSnapshot = () => getServerSnapshot().isGhostwriterPhaseD;
const getIsGhostwriterPhaseEServerSnapshot = () => getServerSnapshot().isGhostwriterPhaseE;
const getPhaseEAllowlistJobKeysServerSnapshot = () => getServerSnapshot().phaseEAllowlistJobKeys;
const getAutoAppliableJobKeysServerSnapshot = () => getServerSnapshot().autoAppliableJobKeys;
const getJobsServerSnapshot = () => getServerSnapshot().jobs;
const getConsentStatusServerSnapshot = () => getServerSnapshot().consentStatus;
const getDraftServerSnapshot = () => getServerSnapshot().draft;

/**
 * Subscribe to only the isGhostwriterActive flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the active bucket, `false` otherwise.
 */
export function useIsGhostwriterActive(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterActiveSnapshot,
    getIsGhostwriterActiveServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterPhaseA flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the phase A (synchronous flow) bucket, `false` otherwise.
 */
export function useIsGhostwriterPhaseA(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterPhaseASnapshot,
    getIsGhostwriterPhaseAServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterPhaseB flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user
 * is in the phase B (skip-eligibility) bucket, `false` otherwise.
 */
export function useIsGhostwriterPhaseB(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterPhaseBSnapshot,
    getIsGhostwriterPhaseBServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterPhaseC flag. Returns `null`
 * while the proctor check is still resolving, `true` if the user is
 * in the phase C bucket (phase A's Prefill flow with the standard
 * 'Auto apply' CTA copy), `false` otherwise. See RN-5091.
 */
export function useIsGhostwriterPhaseC(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterPhaseCSnapshot,
    getIsGhostwriterPhaseCServerSnapshot,
  );
}

/**
 * Subscribe to only the isGhostwriterPhaseD flag. Returns `null` while
 * the proctor check is still resolving, `true` if the user is in the
 * phase D bucket (phase A's Prefill synchronous flow PLUS the CAPTCHA /
 * 2FA intervention screen), `false` otherwise. See RN-3367.
 */
export function useIsGhostwriterPhaseD(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterPhaseDSnapshot,
    getIsGhostwriterPhaseDServerSnapshot,
  );
}

/**
 * `true` when the user should get phase A's "Prefill" synchronous flow
 * and copy. Phase A is the original; phase D is an exact clone that adds
 * the intervention screen, so it inherits every phase-A UI/copy/routing
 * decision. Prefer this over `useIsGhostwriterPhaseA() === true` for any
 * Prefill-experience branch; gate the intervention path itself on
 * `useIsGhostwriterPhaseD()` instead. Treats the still-resolving `null`
 * state as `false` so a slow allocation never briefly shows Prefill copy.
 */
export function useGhostwriterUsesPrefillExperience(): boolean {
  const isPhaseA = useIsGhostwriterPhaseA();
  const isPhaseD = useIsGhostwriterPhaseD();
  return isPhaseA === true || isPhaseD === true;
}

/**
 * Subscribe to only the isGhostwriterPhaseE flag. `null` while resolving,
 * `true` if the user is in the phase E bucket, `false` otherwise.
 */
export function useIsGhostwriterPhaseE(): boolean | null {
  return useSyncExternalStore(
    subscribe,
    getIsGhostwriterPhaseESnapshot,
    getIsGhostwriterPhaseEServerSnapshot,
  );
}

/**
 * Subscribe to only the jobs slice. Does not re-render when
 * consent status or draft changes.
 */
export function useGhostwriterJobs(): AsyncState<ScheduleApplyJob[]> {
  return useSyncExternalStore(subscribe, getJobsSnapshot, getJobsServerSnapshot);
}

/**
 * Subscribe to only the consent status slice. Does not re-render
 * when jobs or draft change.
 */
export function useGhostwriterConsentStatus(): AsyncState<ScheduleApplyConsentStatusResponse> {
  return useSyncExternalStore(subscribe, getConsentStatusSnapshot, getConsentStatusServerSnapshot);
}

export interface UseGhostwriterDraftResult {
  draft: DraftState | null;

  beginDraft: (applicationId: string) => void;
  updateDraftField: (fieldId: string, value: DraftFieldValue) => void;
  getDraftFieldUpdates: () => {
    applicationId: string;
    fieldUpdates: ReadonlyMap<string, DraftFieldValue>;
  } | null;
  discardDraft: () => void;
  validateDraftField: (fieldId: string) => boolean;
  validateAllDraftFields: () => boolean;
  resetAttemptedFields: () => void;
  addGroupInstance: (groupId: string) => string | null;
  removeGroupInstance: (groupId: string, instanceId: string) => void;
}

/**
 * Subscribe to only the draft slice. Does not re-render when
 * jobs or consent status change. Provides draft lifecycle
 * mutations alongside the current draft state.
 */
export function useGhostwriterDraft(): UseGhostwriterDraftResult {
  const draft = useSyncExternalStore(subscribe, getDraftSnapshot, getDraftServerSnapshot);

  return {
    draft,
    beginDraft,
    updateDraftField,
    getDraftFieldUpdates,
    discardDraft,
    validateDraftField,
    validateAllDraftFields,
    resetAttemptedFields,
    addGroupInstance,
    removeGroupInstance,
  };
}

// ---------------------------------------------------------------------------
// Auto-appliable job keys selector hooks
// ---------------------------------------------------------------------------

/**
 * Subscribe to only the autoAppliableJobKeys slice.
 */
export function useAutoAppliableJobKeys(): AsyncState<Set<string>> {
  return useSyncExternalStore(
    subscribe,
    getAutoAppliableJobKeysSnapshot,
    getAutoAppliableJobKeysServerSnapshot,
  );
}

/**
 * Derived hook that checks whether a specific jobKey is in the
 * auto-appliable set. Also exposes `hydrated` to indicate whether
 * Homepage has populated the set at least once.
 */
export function useIsJobAutoAppliable(jobKey: string | null): {
  isAutoAppliable: boolean;
  loading: boolean;
  hydrated: boolean;
} {
  const { data, loading } = useAutoAppliableJobKeys();
  const hydrated = data !== null;
  const isAutoAppliable = jobKey !== null && (data?.has(jobKey) ?? false);
  return { isAutoAppliable, loading, hydrated };
}

/** Subscribe to only the phase_e allowlist (candidate) set. */
export function usePhaseEAllowlistJobKeys(): Set<string> {
  return useSyncExternalStore(
    subscribe,
    getPhaseEAllowlistJobKeysSnapshot,
    getPhaseEAllowlistJobKeysServerSnapshot,
  );
}

/**
 * Derived hook: whether a jobKey is in the phase_e allowlist — gates the
 * ViewJob real-time eligibility read.
 */
export function useIsJobInPhaseEAllowlist(jobKey: string | null): boolean {
  const jobKeys = usePhaseEAllowlistJobKeys();
  return jobKey !== null && jobKeys.has(jobKey);
}

/**
 * Finds a single job by jobKey from the shared jobs slice.
 */
export function useGhostwriterJobByKey(jobKey: string | null): {
  job: ScheduleApplyJob | null;
  loading: boolean;
  error: Error | null;
} {
  const jobs = useGhostwriterJobs();
  const job = jobKey ? (jobs.data?.find((j) => j.jobKey === jobKey) ?? null) : null;
  return { job, loading: jobs.loading, error: jobs.error };
}
